import type { ProbeInfo, VideoStats } from '@shared/protocol';
import { Panel } from './Panel';
import { LevelBadge } from './Status';

const kbps = (b: number | null) => (b === null ? '--' : `${Math.round(b / 1000)} kb/s`);
const f = (v: number | null | undefined, d = 1, unit = '') => (v === null || v === undefined ? '--' : `${v.toFixed(d)}${unit}`);
const clock = (t: number) => new Date(t).toLocaleTimeString('en-GB', { hour12: false });

function flagged(fo: string | null | undefined): string {
  if (!fo) return 'not flagged';
  if (fo === 'progressive') return 'progressive';
  if (fo === 'tt' || fo === 'bt') return 'interlaced, TFF';
  if (fo === 'bb' || fo === 'tb') return 'interlaced, BFF';
  return fo;
}

/** Video health: timing and bitrate from packets, plus decoded-picture analysis. */
export function VideoPanel({ video, probe, analysisEnabled, encoderVisible }: { video: VideoStats | null; probe: ProbeInfo | null; analysisEnabled: boolean; encoderVisible: boolean }) {
  const v = video;
  const a = v?.analysis ?? null;
  const pv = probe?.video[0];
  if (!pv && !v) {
    return (
      <Panel title="Video health">
        <p className="muted">{probe ? 'This stream has no video.' : 'Video measurements appear once the stream is live.'}</p>
      </Panel>
    );
  }
  const scanLevel = !a ? 'info' : a.detectedScan === 'mixed' ? 'warn' : 'pass';
  return (
    <Panel title="Video health">
      <div className="video-grid">
        <div>
          <h4>Timing and bitrate</h4>
          <dl className="kv">
            <dt>Frame rate</dt>
            <dd className="num">
              {f(v?.fps, 2, ' fps')} {v?.nominalFps ? <span className="muted">(stream says {v.nominalFps})</span> : null}
            </dd>
            <dt>Frame timing</dt>
            <dd>{v?.frameDurationCv === null || !v ? '--' : v.frameDurationCv > 0.1 ? <LevelBadge level="warn">Variable</LevelBadge> : 'Constant'}</dd>
            <dt>Dropped / duplicated</dt>
            <dd className="num">
              {v ? `${v.droppedFrames} / ${v.duplicatedFrames}` : '--'}
            </dd>
            <dt>Keyframe interval</dt>
            <dd className="num">
              {f(v?.keyframeInterval, 2, ' s')}
              {v?.keyframeIntervalMin !== null && v?.keyframeIntervalMin !== undefined ? <span className="muted"> (range {f(v.keyframeIntervalMin, 1)} to {f(v.keyframeIntervalMax, 1)} s)</span> : null}
            </dd>
            <dt>Bitrate</dt>
            <dd className="num">
              {kbps(v?.bitrate ?? null)} <span className="muted">avg {kbps(v?.bitrateAvg ?? null)}, {kbps(v?.bitrateMin ?? null)} to {kbps(v?.bitrateMax ?? null)}</span>
            </dd>
            <dt>Rate variation</dt>
            <dd className="num">{v?.bitrateCv === null || !v ? '--' : `${Math.round(v.bitrateCv * 100)} % (CBR is typically below 15 %)`}</dd>
          </dl>
          {!encoderVisible && <p className="muted small">YouTube re-encodes the stream, so these figures describe YouTube's output, not your encoder.</p>}
        </div>
        <div>
          <h4>Picture analysis</h4>
          {!analysisEnabled ? (
            <p className="muted">Decoded-video analysis is switched off for this stream.</p>
          ) : !a ? (
            <p className="muted">Waiting for frames.</p>
          ) : (
            <dl className="kv">
              <dt>Scan (detected)</dt>
              <dd>
                <LevelBadge level={scanLevel}>
                  {a.detectedScan}
                  {a.detectedFieldOrder && a.detectedScan !== 'progressive' ? `, ${a.detectedFieldOrder.toUpperCase()}` : ''}
                </LevelBadge>
              </dd>
              <dt>Scan (flagged)</dt>
              <dd>{flagged(pv?.fieldOrder)}</dd>
              <dt>idet, last 10 s</dt>
              <dd className="num">
                TFF {a.idet.tff}, BFF {a.idet.bff}, progressive {a.idet.progressive}, undetermined {a.idet.undetermined}
              </dd>
              <dt>Combing</dt>
              <dd className="num">{Math.round(a.combing * 100)} % of frames</dd>
              <dt>Black / frozen</dt>
              <dd>
                {a.black ? <LevelBadge level="fail">Black</LevelBadge> : <span>No black</span>}{' '}
                {a.frozen ? <LevelBadge level="fail">Frozen</LevelBadge> : <span>, not frozen</span>}
              </dd>
              <dt>Active picture</dt>
              <dd className="num">{a.crop ? `${a.crop.width}x${a.crop.height}${pv && (a.crop.width < pv.width * 0.9 || a.crop.height < pv.height * 0.9) ? ' (bars detected)' : ''}` : '--'}</dd>
              <dt>Luma (8-bit)</dt>
              <dd className="num">{a.luma ? `min ${a.luma.min}, 10% ${a.luma.low}, avg ${a.luma.avg}, 90% ${a.luma.high}, max ${a.luma.max}` : '--'}</dd>
              <dt>Chroma range</dt>
              <dd className="num">{a.chroma ? `${a.chroma.min} to ${a.chroma.max}` : '--'}</dd>
            </dl>
          )}
          {v && v.events.length > 0 && (
            <>
              <h4>Black and freeze events</h4>
              <ul className="event-list">
                {v.events
                  .slice()
                  .reverse()
                  .slice(0, 8)
                  .map((e, i) => (
                    <li key={i}>
                      <span className="mono">{clock(e.start)}</span> {e.kind === 'black' ? 'Black' : 'Frozen'}{' '}
                      {e.end ? <span className="muted">until {clock(e.end)} ({Math.round((e.end - e.start) / 1000)} s)</span> : <LevelBadge level="fail">ongoing</LevelBadge>}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </Panel>
  );
}
